import type { Patient, Practitioner, Reference } from '@medplum/fhirtypes'
import type { IDTRole } from '@seen/core/models/care-teams/constants'
import { IDT } from '@seen/core/models/care-teams/idt'
import { useMemo } from 'react'
import { useSearchOne } from '#src/hooks/useSearchOne.ts'

interface UseParticipantIdtMembersResult {
  members: { role: IDTRole; practitioner: Reference<Practitioner> }[]
  isPending: boolean
}

// Resolves a participant's IDT members for the given roles, e.g. their RN for
// an RN-visit appointment, or every role a multi-role service could be
// staffed by. Returns one entry per role the IDT actually has a member in,
// in the order the roles were asked for.
export function useParticipantIdtMembers(
  participant: Reference<Patient> | undefined,
  roles: IDTRole[]
): UseParticipantIdtMembersResult {
  const { data: idt, isPending } = useSearchOne(
    IDT,
    participant ? IDT.byParticipant(participant) : undefined
  )

  const members = useMemo(() => {
    if (!idt) {
      return []
    }
    return roles.flatMap((role) => {
      const practitioner = idt.getMemberByRole(role)?.practitioner
      return practitioner ? [{ role, practitioner }] : []
    })
  }, [idt, roles])

  return { members, isPending }
}

interface UseParticipantIdtMemberResult {
  member: Reference<Practitioner> | undefined
  isPending: boolean
}

// Single-role convenience wrapper.
export function useParticipantIdtMember(
  participant: Reference<Patient> | undefined,
  role: IDTRole | undefined
): UseParticipantIdtMemberResult {
  const roles = useMemo(() => (role ? [role] : []), [role])
  const { members, isPending } = useParticipantIdtMembers(participant, roles)
  return { member: members[0]?.practitioner, isPending }
}
